/**
 * Watching a project's changes as they happen — UX-05.
 *
 * "Is realtime working?" had no answer anywhere until now: the SDK can
 * subscribe, but somebody who wanted to KNOW had to write an app first.
 *
 * The browser never sees a service key. It asks the control plane for a
 * ticket, which is signed for one channel and lasts five minutes, and the
 * socket opens with that. See the route added alongside this page.
 *
 * Every judgement lives in lib/realtime.ts, where tests hold it.
 */

import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useApi, useAsync } from '../lib/useApi.js'
import { ApiError } from '../lib/api.js'
import { Empty, ErrorBox, PageHeader } from '../components/ui.js'
import {
  addEvent, closeReason, connectionSummary, describeEvent, resolveChannel, tableChannel,
  type Connection, type LiveEvent,
} from '../lib/realtime.js'

export default function Realtime() {
  const { id = '' } = useParams()
  const api = useApi()
  const schema = useAsync(() => api.projectSchema(id), [api, id])

  const [channel, setChannel] = useState('')
  const [connection, setConnection] = useState<Connection>({ kind: 'idle' })
  const [events, setEvents] = useState<LiveEvent[]>([])
  const [error, setError] = useState<string | null>(null)
  const socket = useRef<WebSocket | null>(null)
  const sawEvents = useRef(false)

  // Whatever happens, do not leave a socket open behind a closed tab.
  useEffect(() => () => socket.current?.close(), [])

  async function watch(target: string) {
    socket.current?.close()
    setEvents([])
    setError(null)
    sawEvents.current = false
    setConnection({ kind: 'connecting' })

    let ticket
    try {
      ticket = await api.realtimeTicket(id, target)
    } catch (err) {
      setConnection({ kind: 'idle' })
      setError(err instanceof ApiError ? err.message : 'Could not get a ticket for that channel.')
      return
    }

    const ws = new WebSocket(ticket.url)
    socket.current = ws
    ws.addEventListener('open', () => setConnection({ kind: 'open', expiresAt: ticket.expiresAt }))
    ws.addEventListener('message', (e) => {
      let parsed: unknown
      try { parsed = JSON.parse(String(e.data)) } catch { return }
      const event = describeEvent(parsed, new Date().toISOString())
      if (!event) return
      sawEvents.current = true
      setEvents((previous) => addEvent(previous, event))
    })
    ws.addEventListener('close', (e) => {
      setConnection({ kind: 'closed', reason: closeReason(e.code, sawEvents.current) })
    })
  }

  const tables = schema.data?.schema.tables ?? []
  const watching = connection.kind === 'open' || connection.kind === 'connecting'

  return (
    <>
      <PageHeader title="Realtime" />
      <p className="sub">
        Changes as they happen. Your browser never holds a service key — it asks for a ticket,
        signed for one channel and good for five minutes.
      </p>

      <div style={{ margin: '16px 0' }}>
        <label htmlFor="realtime-channel" className="sub" style={{ display: 'block', marginBottom: 6 }}>
          Channel
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            id="realtime-channel"
            className="input"
            placeholder="A channel, or pick a table"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <button
            className="btn btn-primary"
            disabled={channel.trim() === '' || watching}
            onClick={() => {
              const target = resolveChannel(channel, tables.map((t) => t.name))
              setChannel(target)
              void watch(target)
            }}
          >
            {connection.kind === 'connecting' ? 'Opening…' : 'Watch'}
          </button>
          {watching && (
            <button className="btn" onClick={() => socket.current?.close()}>Stop</button>
          )}
        </div>
      </div>

      {tables.length > 0 && (
        <p className="sub small">
          Tables:{' '}
          {tables.map((t) => (
            <button
              key={t.name}
              className="btn btn-small"
              style={{ marginRight: 6 }}
              disabled={watching}
              onClick={() => {
                const target = tableChannel(t.name)
                setChannel(target)
                void watch(target)
              }}
            >
              {t.name}
            </button>
          ))}
        </p>
      )}

      <p className="sub small">
        Row changes use <code>table:&lt;name&gt;</code>. Entering an exact table name selects that
        stream automatically; any other name is a custom message channel.
      </p>

      {error && <ErrorBox>{error}</ErrorBox>}
      <p className="sub">{connectionSummary(connection, events.length)}</p>

      {events.length === 0 ? (
        <Empty>
          Nothing yet. Change a row — from the Database page, the CLI, or your app — and it
          appears here.
        </Empty>
      ) : (
        <table className="table">
          <thead>
            <tr><th>at</th><th>what</th><th>where</th></tr>
          </thead>
          <tbody>
            {events.map((e, i) => (
              <tr key={`${e.at}-${i}`}>
                <td className="sub small mono">{e.at.slice(11, 19)}</td>
                <td>{e.detail}</td>
                <td className="mono">{e.subject}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}
